import {IReportDataObject, emptyReportDataObject} from "../../entities/reports/IReportData";
import {FetchState} from "../../entities/FetchState";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import { IError } from "../../utils/callGraphQl";
import Either from "../../utils/Either";
import {callApiWithResult, callApiNoResult, ICallResult, ICallNoResult} from "../../utils/callApi";


/**********************************************************
 * API Data
 **********************************************************/
export interface IData {
    streamId: string;
    name: string;
    erkenningsnummer: string;
    hasValidVaphJwtToken: boolean;
}

export const defaultCompanyInfo: IData = {
    streamId: '',
    name: '',
    erkenningsnummer: '',
    hasValidVaphJwtToken: false
};

export const defaultCompanyInfoObject = emptyReportDataObject<IData>(defaultCompanyInfo);

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    CompanyInfo: IReportDataObject<IData>;
    SaveVaphToken: IReportDataObject<IData>;
}

export const InitialState: IState = {
    CompanyInfo: defaultCompanyInfoObject,
    SaveVaphToken: defaultCompanyInfoObject
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    // Fetch company info action types
    FetchData = "companies.CompanyInfo Fetch",
    FetchDataSuccess = "companies.CompanyInfo FetchSuccess",
    FetchDataFailed = "companies.CompanyInfo FetchFailed",
    // Save VAPH token action types
    SaveVaphToken = "companies.CompanyInfo SaveVaphToken",
    SaveVaphTokenSuccess = "companies.CompanyInfo SaveVaphTokenSuccess",
    SaveVaphTokenFailed = "companies.CompanyInfo SaveVaphTokenFailed"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}

export interface ISaveVaphToken {
    type: ActionTypes.SaveVaphToken,
    payload: { vaphToken: string }
}
export interface ISaveVaphTokenSuccess {
    type: ActionTypes.SaveVaphTokenSuccess,
    payload: { data: IData }
}
export interface ISaveVaphTokenFailed {
    type: ActionTypes.SaveVaphTokenFailed,
    payload: { errorMessage: string }
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed 
    | ISaveVaphToken | ISaveVaphTokenSuccess | ISaveVaphTokenFailed;

export class ActionCreators {
    public static FetchData(): IFetchData {
        return { 
            type: ActionTypes.FetchData
        };
    }
    public static FetchDataSuccess(newData: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
    
    public static SaveVaphToken(vaphToken: string): ISaveVaphToken {
        return {
            type: ActionTypes.SaveVaphToken,
            payload: { vaphToken }
        };
    }
    public static SaveVaphTokenSuccess(newData: IData): ISaveVaphTokenSuccess {
        return {
            type: ActionTypes.SaveVaphTokenSuccess,
            payload: { data: newData }
        };
    }
    public static SaveVaphTokenFailed(err: string): ISaveVaphTokenFailed {
        return {
            type: ActionTypes.SaveVaphTokenFailed,
            payload: { errorMessage: err }
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                CompanyInfo: {
                    ...state.CompanyInfo,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                CompanyInfo: {
                    ...state.CompanyInfo,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                CompanyInfo: {
                    ...state.CompanyInfo,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        case ActionTypes.SaveVaphToken:
            return {
                ...state,
                SaveVaphToken: {
                    ...state.SaveVaphToken,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.SaveVaphTokenSuccess:
            return {
                ...state,
                SaveVaphToken: {
                    ...state.SaveVaphToken,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                },
                // Update company info with new token status
                CompanyInfo: {
                    ...state.CompanyInfo,
                    data: action.payload.data
                }
            };
        case ActionTypes.SaveVaphTokenFailed:
            return {
                ...state,
                SaveVaphToken: {
                    ...state.SaveVaphToken,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleFetchData(_action: IFetchData) {
    try {
        const response : Either<IError, ICallResult<IData[]>> = yield call(callApiWithResult, 'GET', '/v1/Companies');
        yield response
            .FlatMap(success => {
                // The API returns an array, we take the first element
                const companyData = success.result && success.result.length > 0 
                    ? success.result[0] 
                    : defaultCompanyInfo;
                return put(ActionCreators.FetchDataSuccess(companyData) as any);
            })
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleSaveVaphToken(action: ISaveVaphToken) {
    try {
        const response : Either<IError, ICallNoResult> = yield call(
            callApiNoResult, 
            'PUT', 
            '/v1/Companies/vaphtoken',
            { vaphToken: action.payload.vaphToken }
        );
        
        yield response
            .FlatMap(_success => {
                // Dispatch success action to transition SaveVaphToken state
                return put(ActionCreators.SaveVaphTokenSuccess(defaultCompanyInfo) as any);
            })
            .Reduce(error => put(ActionCreators.SaveVaphTokenFailed(error.text) as any));
        
        // If save was successful, fetch updated company data
        if (response.IsRight()) {
            yield put(ActionCreators.FetchData());
        }
    } catch (err) {
        yield put(ActionCreators.SaveVaphTokenFailed(JSON.stringify(err)))
    }
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleFetchData);
    yield takeEvery(ActionTypes.SaveVaphToken, handleSaveVaphToken);
}

export function* sagas() {
    yield all([fork(watchAll)]);
}

